'use client';

import { ServerActionName } from '@headless-workspace/config';
import {
    AsyncData,
    ContentAssetKeys,
    ResultEmpty,
    ResultFailure,
    ResultSuccess,
    unwrapResultSuccess,
} from '@headless-workspace/core-domain';
import { StringHelpers } from '@headless-workspace/utils';
import { ContentAssetType, getContentAssetServerAction } from '../../../server';
import { useSafeSWR } from '../hooks';
import { useClientData } from '../providers';
import { UserRevalidationKey } from '../user';

const LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR_KEY = 'LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR';
const LEGAL_MENTIONS_ASSET_MESSAGE_ERROR = 'An unexpected error occurred while fetching the VAT legal mentions.';

export const useVATLegalMentionsAssets = (): AsyncData<ContentAssetType> => {
    const assetKey = ContentAssetKeys.VATLegalMentions;
    const { getContentAssets } = useClientData();
    const contentAsset = getContentAssets(assetKey);

    const {
        data: swrData,
        error,
        isLoading,
    } = useSafeSWR(
        contentAsset ? null : UserRevalidationKey.LegalMentionsAsset,
        ServerActionName.getContentAssetServerAction,
        () => getContentAssetServerAction([assetKey]),
        { fallbackData: contentAsset },
    );

    if (contentAsset) {
        return {
            isLoading,
            result: ResultSuccess(contentAsset),
        };
    }

    if (swrData?.type !== 'success' || swrData.data.length === 0) {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    LEGAL_MENTIONS_ASSET_NOT_FOUND_ERROR_KEY,
                    error instanceof Error ? error.message : LEGAL_MENTIONS_ASSET_MESSAGE_ERROR,
                ),
            };
        }

        return {
            isLoading,
            result: ResultEmpty(),
        };
    }

    const assetList = unwrapResultSuccess(swrData);
    const legalMentionAsset = assetList?.find((item) => item.id === assetKey);
    const htmlContent = StringHelpers.trim(legalMentionAsset?.content?.html?.content);

    return {
        isLoading,
        result: legalMentionAsset && htmlContent ? ResultSuccess(legalMentionAsset) : ResultEmpty(),
    };
};
